import { SiteFooter } from '@/components/layout/site-footer'
import { BreadcrumbJsonLd } from '@/components/seo/breadcrumb-json-ld'
import { PageMeta } from '@/components/seo/page-meta'
import { WhitepaperJsonLd } from '@/components/seo/whitepaper-json-ld'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import apiClient from '@/lib/api-client'
import { getPelgoWhitepaperSeo } from '@/lib/pelgo-seo'
import { whitepaperHeroOverlayStyle } from '@/lib/whitepaper-hero'
import { type WhitepaperLeadFormData, whitepaperLeadSchema } from '@/lib/whitepaper-lead-schema'
import { getWhitepaper, getWhitepaperLandingPath } from '@/lib/whitepapers'
import { NotFoundPage } from '@/pages/not-found'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'

const WHITEPAPER_BOTTOM_CTA_POSTER_SRC = '/assets/landing/feature-outplacement.webp'

export function WhitepaperLandingPage() {
  const { slug } = useParams<{ slug: string }>()
  const whitepaper = slug ? getWhitepaper(slug) : undefined
  const [submitSuccess, setSubmitSuccess] = useState(false)

  useEffect(() => {
    void slug
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [slug])

  useEffect(() => {
    void slug
    const videos = document.querySelectorAll('video')
    for (const video of videos) {
      video.muted = true
      video.volume = 0
      video.loop = true
      video.playsInline = true
      video.setAttribute('playsinline', 'true')
      video.setAttribute('muted', 'true')
      video.preload = 'auto'
      video.defaultMuted = true
      video.controls = false
      video.removeAttribute('controls')
      video.style.pointerEvents = 'none'

      const attemptPlay = async () => {
        try {
          video.muted = true
          video.volume = 0
          video.controls = false
          video.removeAttribute('controls')
          await video.play()
        } catch {
          // Autoplay blocked; will retry on interaction
        }
      }

      attemptPlay()
      video.addEventListener('canplaythrough', attemptPlay, { once: true })
    }
  }, [slug])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<WhitepaperLeadFormData>({
    resolver: zodResolver(whitepaperLeadSchema),
  })

  if (!whitepaper) {
    return <NotFoundPage />
  }

  const seoConfig = getPelgoWhitepaperSeo(whitepaper.slug)
  if (!seoConfig) {
    return <NotFoundPage />
  }

  const onSubmit = (data: WhitepaperLeadFormData) => {
    apiClient
      .post('/api/v1/whitepaper/request', {
        name: data.name,
        company: data.company,
        email: data.email,
        whitepaper_slug: whitepaper.slug,
      })
      .catch(() => undefined)

    setSubmitSuccess(true)
    reset()
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={seoConfig} />
      <WhitepaperJsonLd whitepaper={whitepaper} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', path: '/' },
          { name: 'Whitepapers', path: '/resources/whitepapers' },
          { name: whitepaper.title, path: getWhitepaperLandingPath(whitepaper.slug) },
        ]}
      />
      <section className="relative overflow-hidden min-h-[600px] md:min-h-[800px] flex flex-col">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img alt="" className="absolute w-full h-full object-cover" src={whitepaper.heroBgSrc} />
          <div className="absolute inset-0" style={whitepaperHeroOverlayStyle} />
        </div>

        <nav className="relative z-10 w-full px-6 md:px-16 py-6 md:py-8">
          <div className="max-w-[1280px] mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/logo-white.png" alt="Pelgo" className="h-8 md:h-10" />
            </Link>
            <Button
              asChild
              className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold"
            >
              <Link to="/">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Pelgo.com
              </Link>
            </Button>
          </div>
        </nav>

        <div className="relative z-10 flex-1 flex items-center px-6 md:px-16 pb-12 md:pb-0 scroll-fade-up in-view">
          <div className="max-w-[1280px] mx-auto w-full flex flex-col md:flex-row items-center md:items-center justify-between gap-8 md:gap-16">
            <div className="flex flex-col gap-4 md:gap-16 md:w-[550px]">
              <div className="flex flex-col gap-3 md:gap-4">
                <p className="text-xs font-normal text-white tracking-[0.24px] leading-[1.5] uppercase">
                  {whitepaper.eyebrow}
                </p>
                <h1 className="text-3xl md:text-[48px] font-display text-white leading-[1.2] tracking-[-0.96px]">
                  {whitepaper.titleParts.prefix}
                  <span className="text-pelgo-blue-light">{whitepaper.titleParts.accent}</span>
                  {whitepaper.titleParts.suffix}
                </h1>
                <p className="text-sm md:text-base font-normal text-white leading-[1.5] tracking-[-0.32px] max-w-[768px]">
                  {whitepaper.heroDescription}
                </p>
              </div>
            </div>

            <div className="w-full md:w-[480px] bg-white rounded-2xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)] p-6 md:p-8">
              {submitSuccess ? (
                <div className="flex flex-col gap-4 items-center text-center py-4">
                  <h3 className="text-2xl md:text-[32px] font-display text-gray-900 leading-none tracking-[-0.64px]">
                    Thank you!
                  </h3>
                  <p className="text-base text-gray-500 leading-[1.5]">
                    Check your email for the download link.
                  </p>
                  <p className="text-sm text-gray-500 leading-[1.5]">
                    If the email button does not open, an ad blocker may be stopping the
                    sender&apos;s link redirect. Pause the blocker for that message or use the
                    plain-text copy of the direct{' '}
                    <span className="whitespace-nowrap">pelgo.com</span> link.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                  <h3 className="text-2xl md:text-[32px] font-display text-gray-900 leading-none tracking-[-0.64px]">
                    Get your free whitepaper.
                  </h3>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-0.5 items-start">
                      <Label
                        htmlFor="wp-name"
                        className="text-sm font-normal text-[#414651] tracking-[-0.28px]"
                      >
                        Name
                      </Label>
                      <span className="text-sm font-medium text-[#7f56d9]">*</span>
                    </div>
                    <Input
                      id="wp-name"
                      type="text"
                      placeholder="Jack Smith"
                      {...register('name')}
                      className="w-full rounded-lg border-[#d5d7da] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
                      aria-invalid={errors.name ? 'true' : 'false'}
                    />
                    {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-0.5 items-start">
                      <Label
                        htmlFor="wp-company"
                        className="text-sm font-normal text-[#414651] tracking-[-0.28px]"
                      >
                        Company
                      </Label>
                      <span className="text-sm font-medium text-[#7f56d9]">*</span>
                    </div>
                    <Input
                      id="wp-company"
                      type="text"
                      placeholder="Acme Inc."
                      {...register('company')}
                      className="w-full rounded-lg border-[#d5d7da] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
                      aria-invalid={errors.company ? 'true' : 'false'}
                    />
                    {errors.company && (
                      <p className="text-sm text-red-600">{errors.company.message}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-0.5 items-start">
                      <Label
                        htmlFor="wp-email"
                        className="text-sm font-normal text-[#414651] tracking-[-0.28px]"
                      >
                        Email
                      </Label>
                      <span className="text-sm font-medium text-[#7f56d9]">*</span>
                    </div>
                    <Input
                      id="wp-email"
                      type="email"
                      placeholder="jack@pelgo.com"
                      {...register('email')}
                      className="w-full rounded-lg border-[#d5d7da] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
                      aria-invalid={errors.email ? 'true' : 'false'}
                    />
                    {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#0050e6] hover:bg-[#0050e6]/90 text-white rounded-full px-6 py-3 text-base font-semibold uppercase min-h-[48px]"
                  >
                    Send Download Link
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-12 md:py-16 px-6 md:px-16 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.04) 100%), linear-gradient(90deg, #fff 0%, #fff 100%)',
        }}
      >
        <div className="max-w-[1280px] mx-auto flex flex-col gap-8 md:gap-16">
          <div className="flex flex-col gap-6 md:gap-8">
            <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 md:items-end">
              <div className="flex-1 flex flex-col gap-4">
                <h2 className="text-3xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                  What You'll Learn Inside
                </h2>
                <p className="text-base font-normal text-gray-500 leading-[1.5] tracking-[-0.32px]">
                  {whitepaper.learnIntro}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-8 md:gap-8 items-start md:items-center">
            <div className="flex-1 flex flex-col gap-6 md:gap-8">
              {whitepaper.learnItems.map((item) => (
                <div key={item.title} className="flex flex-col gap-2 py-0.5">
                  <h3 className="text-base md:text-lg font-normal text-gray-900 leading-[1.5] tracking-[-0.36px]">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base font-normal text-gray-500 leading-[1.5] tracking-[-0.32px]">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex-1 flex items-center justify-center">
              <img
                src={`${whitepaper.coverImageSrc}?v=${whitepaper.slug}-layer1`}
                alt={whitepaper.coverImageAlt}
                loading="lazy"
                className="w-full max-w-[502px] h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-white scroll-fade-up">
        <div className="relative w-full px-6 md:px-24 py-16 md:py-32 overflow-hidden flex items-center justify-center min-h-[300px] md:min-h-[500px]">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={WHITEPAPER_BOTTOM_CTA_POSTER_SRC}
            className="absolute max-w-none object-cover w-full h-full"
            controlsList="nodownload"
          >
            <source
              src="https://videos.pexels.com/video-files/853870/853870-hd_1920_1080_25fps.mp4"
              type="video/mp4"
            />
            <img
              src={WHITEPAPER_BOTTOM_CTA_POSTER_SRC}
              alt=""
              className="absolute max-w-none object-cover w-full h-full"
            />
          </video>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative flex flex-col gap-4 md:gap-8 items-center text-center max-w-[1280px] w-full">
            <div className="flex flex-col gap-4 items-center text-white">
              <h2 className="text-2xl md:text-[64px] font-display text-white leading-none tracking-[-0.6px] md:tracking-[-1.28px]">
                We provide every departure in every industry with effective outplacement support.
              </h2>
              <p className="text-sm md:text-xl font-normal text-white leading-[1.5] max-w-[768px] tracking-[-0.32px] md:tracking-[-0.4px]">
                We do so by providing a powerful suite of tools at a fraction of what traditional
                outplacement services cost.
              </p>
            </div>
            <Button
              asChild
              className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-base font-semibold min-h-[48px]"
            >
              <Link to="/contact">Request Free Trial</Link>
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
